/**
 * altenotTts.ts — Wrapper TS del plugin nativo de voz (contestadora) del POS.
 *
 * QUÉ HACE: Expone `isAvailable`, `speak` y `stop` del plugin Android
 *   `AltenotTts` (TextToSpeech nativo) y define no-ops para Web/iOS, donde la
 *   voz usa Web Speech Synthesis. `isNativeTtsPlatform()` indica si la
 *   plataforma usa el motor nativo.
 * QUÉ INVOCA: @capacitor/core (registerPlugin, Capacitor).
 * QUÉ EXPORTA: AltenotTts, AltenotTtsPlugin, AltenotTtsAvailability,
 *   AltenotTtsSpeakOptions, AltenotTtsSpeakResult, isNativeTtsPlatform.
 * QUÉ NO DEBE HACER: No decide qué anunciar ni persiste preferencias.
 */
import { Capacitor, registerPlugin } from "@capacitor/core";

export interface AltenotTtsAvailability {
  /** El motor TextToSpeech del equipo está listo. */
  available: boolean;
  /** Idioma aplicado (ej. "es-CO"); null si no hay motor. */
  language: string | null;
}

export interface AltenotTtsSpeakOptions {
  text: string;
  /** Volumen 0–1 (por defecto 1). */
  volume?: number;
  /** Velocidad 0.5–2 (por defecto 1). */
  rate?: number;
  /** Tono 0.5–2 (por defecto 1). */
  pitch?: number;
  /** true = corta lo que esté sonando; false = encola (por defecto true). */
  flush?: boolean;
}

export interface AltenotTtsSpeakResult {
  /** La frase terminó de reproducirse (false si falló o se canceló). */
  spoken: boolean;
}

export interface AltenotTtsPlugin {
  isAvailable(): Promise<AltenotTtsAvailability>;
  speak(options: AltenotTtsSpeakOptions): Promise<AltenotTtsSpeakResult>;
  stop(): Promise<void>;
}

const noopImpl = () => ({
  isAvailable: async (): Promise<AltenotTtsAvailability> => ({
    available: false,
    language: null,
  }),
  speak: async (): Promise<AltenotTtsSpeakResult> => ({ spoken: false }),
  stop: async (): Promise<void> => undefined,
});

export const AltenotTts = registerPlugin<AltenotTtsPlugin>("AltenotTts", {
  web: noopImpl,
  ios: noopImpl,
});

/** La plataforma tiene motor TTS nativo (hoy: Android/APK). */
export const isNativeTtsPlatform = (): boolean =>
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
